import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import { NAVY, RADIUS_CARD, SHADOW_CARD, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

interface InfoSectionProps {
  title: string;
  icon: React.ReactElement;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  variant?: 'default' | 'compact';
  expanded?: boolean;
  onChange?: (expanded: boolean) => void;
  hideSummary?: boolean;
  /** When true, children only mount on first expansion (keeps mounted afterwards). */
  lazy?: boolean;
}

const InfoSection: React.FC<InfoSectionProps> = ({
  title,
  icon,
  children,
  defaultExpanded = false,
  variant = 'default',
  expanded: externalExpanded,
  onChange = undefined,
  hideSummary = false,
  lazy = false,
}) => {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);

  const expanded = externalExpanded !== undefined ? externalExpanded : internalExpanded;

  const handleChange = (_: React.SyntheticEvent, isExpanded: boolean) => {
    if (onChange) {
      onChange(isExpanded);
    } else {
      setInternalExpanded(isExpanded);
    }
  };

  if (hideSummary) {
    return <AccordionDetails sx={{ pt: 0.5, pb: 0.5 }}>{children}</AccordionDetails>;
  }

  const tintedIcon = React.isValidElement<{ sx?: Record<string, unknown> }>(icon)
    ? React.cloneElement(icon, {
        sx: { color: alpha(NAVY, 0.6), fontSize: 20, ...(icon.props.sx ?? {}) },
      })
    : icon;

  return (
    <Accordion
      expanded={expanded}
      onChange={handleChange}
      disableGutters
      slotProps={lazy ? { transition: { mountOnEnter: true } } : undefined}
      sx={{
        mb: 1,
        boxShadow: SHADOW_CARD,
        backgroundColor: SURFACE_CONTAINER_LOWEST,
        borderRadius: RADIUS_CARD,
        '&:first-of-type, &:last-of-type': { borderRadius: RADIUS_CARD },
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: alpha(NAVY, 0.55) }} />}
        sx={{
          backgroundColor: SURFACE_CONTAINER_LOWEST,
          borderRadius: expanded ? `${RADIUS_CARD} ${RADIUS_CARD} 0 0` : RADIUS_CARD,
          minHeight: (() => {
            if (expanded) {
              return variant === 'compact' ? 36 : 42;
            }
            return variant === 'compact' ? 34 : 40;
          })(),
          px: 2,
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            gap: 1,
            margin: expanded ? '6px 0' : '5px 0',
          },
          transition: 'background-color 0.2s ease-in-out',
        }}
      >
        {tintedIcon}
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            fontSize: '0.95rem',
            color: NAVY,
          }}
        >
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0.5, pb: 1.5, px: 2 }}>{children}</AccordionDetails>
    </Accordion>
  );
};

export default InfoSection;
